import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NotificationGroupInputProps, Option } from './NotificationGroupInput';
import rpcShared from '@rockpapercoin/rpc-shared';
import { gql } from '@apollo/client';
import {
	UseNotificationGroupEmailNotificationGroupFragment,
	UseNotificationGroupOrganizationFragment,
	UseNotificationGroupOrganizationForGetContractWhereFragment,
	EmailNotificationGroupTitle,
} from '#types';

export const useNotificationGroupEmailNotificationGroupFragment = gql`
	fragment useNotificationGroupEmailNotificationGroup on EmailNotificationGroup {
		id
		members {
			id
			email
			userType
			orgUser {
				id
				firstName
				lastName
				role
				organization {
					id
				}
			}
		}
	}
`;

export const useNotificationGroupOrganizationFragment = gql`
	fragment useNotificationGroupOrganization on Organization {
		id
		users {
			id
			firstName
			lastName
			role
			organization {
				id
			}
			user {
				id
				email
				userType
			}
		}
	}
`;

export const useNotificationGroupOrganizationForGetContractWhereFragment = gql`
	fragment useNotificationGroupOrganizationForGetContractWhere on OrganizationForGetContractWhere {
		id
		users {
			id
			firstName
			lastName
			role
			organization {
				id
			}
			user {
				id
				email
				userType
			}
		}
	}
`;

type Validation = NotificationGroupInputProps['validation'];

type OnChangeValue = {
	emailNotificationGroup: UseNotificationGroupEmailNotificationGroupFragment & {
		id: string;
		groupTitle: EmailNotificationGroupTitle;
	};
};

export type UseNotificationGroupProps = {
	value?: UseNotificationGroupEmailNotificationGroupFragment | null;
	id: string;
	groupTitle: EmailNotificationGroupTitle;
	onChange: ( value: OnChangeValue ) => void;
	organization?:
	| UseNotificationGroupOrganizationFragment
	| UseNotificationGroupOrganizationForGetContractWhereFragment
	| null;
	hideSuggestions?: true;
};

type EmailNotificationGroupLike = {
	id: string;
	groupTitle?: EmailNotificationGroupTitle;
	members?: Array<{ email: string }>;
};

const emailNotificationGroupMatches = (
	a: EmailNotificationGroupLike | null | undefined,
	b: EmailNotificationGroupLike | null | undefined
): boolean => {
	if ( a === b ) {
		return true;
	}
	if ( !a || !b ) {
		return !a?.members?.length && !b?.members?.length;
	}
	if (
		a.id !== b.id ||
		( a.groupTitle !== undefined &&
			b.groupTitle !== undefined &&
			a.groupTitle !== b.groupTitle )
	) {
		return false;
	}
	const aEmails = [ ...( a.members ?? [] ).map( ( m ) => m.email ) ].sort();
	const bEmails = [ ...( b.members ?? [] ).map( ( m ) => m.email ) ].sort();
	if ( aEmails.length !== bEmails.length ) {
		return false;
	}
	return aEmails.every( ( email, i ) => email === bEmails[ i ] );
};

export const useNotificationGroup = ( {
	hideSuggestions,
	organization,
	id,
	groupTitle,
	value: propsValue,
	onChange: onPropsChange,
}: UseNotificationGroupProps ) => {
	const [ value, setValue ] = useState<Option[]>( [] );
	const [ validation, setValidation ] = useState<Validation>();

	// some special state to avoid a render loop between the reflectedValue and the propsValue
	const propsRef = useRef( { propsValue, onPropsChange } );
	propsRef.current = { propsValue, onPropsChange };
	const userHasChangedValueRef = useRef( false );

	const defaultValue = useMemo(
		() =>
			propsValue?.members.map( ( user ) => ( {
				value: user.email,
				label:
					user.orgUser && organization?.id === user.orgUser.organization?.id
						? `${ user.orgUser.firstName } ${ user.orgUser.lastName }`
						: user.email,
				userType: user.userType,
				role: user.orgUser ? user.orgUser.role : undefined,
			} ) ) || [],
		[ organization?.id, propsValue?.members ]
	);

	useEffect( () => {
		setValue( defaultValue );
	}, [ defaultValue ] );

	const onChange = useCallback(
		(
			optionOrOptions: Parameters<NotificationGroupInputProps['onChange']>[0]
		) => {
			userHasChangedValueRef.current = true;
			setValue( ( prevState ) => {
				const newState = [ ...prevState ];
				if ( !optionOrOptions ) {
					return prevState;
				}
				if ( Array.isArray( optionOrOptions ) ) {
					return [ ...optionOrOptions ];
				} else if ( optionOrOptions ) {
					const itemExists = !!prevState.find(
						( option ) => option.value === optionOrOptions.value
					);
					if ( itemExists ) {
						return prevState;
					} else {
						newState.push( optionOrOptions );
					}
				}
				return newState;
			} );
		},
		[]
	);

	useEffect( () => {
		if ( !userHasChangedValueRef.current ) {
			return;
		}
		const {
			propsValue: currentPropsValue,
			onPropsChange: currentOnPropsChange,
		} = propsRef.current;
		const base = currentPropsValue ?? null;
		const membersFromValue = ( value ?? [] ).map( ( option ) => {
			const existing = base?.members?.find( ( m ) => m.email === option.value );
			return existing
				? { ...existing, email: option.value, userType: option.userType }
				: {
					id: option.value,
					email: option.value,
					userType: option.userType,
					orgUser: undefined,
				  };
		} );
		const reflectedValue: OnChangeValue = {
			emailNotificationGroup: {
				...( base ?? {} ),
				id: base?.id ?? id,
				groupTitle,
				members: membersFromValue,
			},
		};
		if (
			emailNotificationGroupMatches(
				reflectedValue.emailNotificationGroup,
				currentPropsValue
			)
		) {
			userHasChangedValueRef.current = false;
			return;
		}
		currentOnPropsChange( reflectedValue );
		// Only re-run when local value or ids change; read props from ref so we're not out of sync with what we just pushed.
	}, [
		value,
		id,
		groupTitle
	] );

	const onValidate = useCallback( () => {
		const emailAddresses: string[] = value.reduce(
			( acc, option ) => [ ...acc, option.value ],
			[] as string[]
		);
		const invalidEmailAddresses = emailAddresses.filter(
			( email ) => !rpcShared.regex.emailAddress.test( email )
		);
		if ( invalidEmailAddresses.length > 0 ) {
			setValidation( {
				errorText: `Email address${
					invalidEmailAddresses.length > 1 ? 'es' : ''
				} are invalid`,
				emailAddresses: invalidEmailAddresses,
			} );
			return false;
		} else {
			setValidation( ( prevState ) => ( prevState ? undefined : prevState ) );
			return true;
		}
	}, [ value ] );

	return {
		value,
		members: hideSuggestions
			? []
			: ( ( organization?.users?.map( ( orgUser ) => ( {
				value: orgUser.user?.email,
				label: `${ orgUser.firstName } ${ orgUser.lastName }`,
				userType: orgUser.user?.userType,
				role: orgUser.role,
			  } ) ) || [] ) as Option[] ),
		validation,
		onChange,
		onValidate,
		setValidation,
	};
};

export type UseNotificationGroupHookResults = ReturnType<
	typeof useNotificationGroup
>;
